import React, { useState, useEffect } from 'react';
import { ExternalLink, X } from 'lucide-react';

const HomepageBannerAd = () => {
  const [bannerAd, setBannerAd] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);

  const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

  useEffect(() => {
    fetchBannerAd();
  }, []);

  const fetchBannerAd = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/advertisements`);
      if (response.ok) {
        const data = await response.json();
        // Find the first active banner ad
        const banner = (data.advertisements || []).find(ad => ad.ad_type === 'banner' && ad.is_active);
        setBannerAd(banner || null);
        
        // Track impression if banner exists
        if (banner) {
          fetch(`${API_BASE}/api/advertisements/${banner.id}/impression`, { method: 'POST' });
        }
      }
    } catch (error) {
      console.error('Error fetching banner ad:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBannerClick = async () => {
    if (!bannerAd) return;
    
    // Track click
    try {
      await fetch(`${API_BASE}/api/advertisements/${bannerAd.id}/click`, { method: 'POST' });
    } catch (error) {
      console.error('Error tracking click:', error);
    }
    
    // Open link
    if (bannerAd.link_url) {
      window.open(bannerAd.link_url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleDismiss = (e) => {
    e.stopPropagation();
    setDismissed(true);
  };

  // Don't render if loading, no banner, or dismissed
  if (loading || !bannerAd || dismissed) {
    return null;
  }

  return (
    <div className="max-w-7xl mx-auto px-3 sm:px-4 mb-4 sm:mb-5">
      <div 
        className="relative bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 rounded-xl overflow-hidden cursor-pointer group shadow-lg hover:shadow-xl transition-all duration-300"
        onClick={handleBannerClick}
      >
        {/* Dismiss button */}
        <button
          onClick={handleDismiss}
          className="absolute top-2 right-2 z-10 p-1.5 bg-black/50 hover:bg-black/70 rounded-full text-white/70 hover:text-white transition-colors"
          title="Dismiss ad"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Sponsored label */}
        <div className="absolute top-2 left-2 z-10">
          <span className="text-[10px] uppercase tracking-wider text-white/50 bg-black/30 px-2 py-0.5 rounded">
            Sponsored
          </span>
        </div>

        <div className="flex flex-col sm:flex-row items-center">
          {/* Image section */}
          {bannerAd.image_url && (
            <div className="w-full sm:w-1/3 md:w-1/4 h-32 sm:h-36 flex-shrink-0 bg-white/5 flex items-center justify-center p-4">
              <img
                src={bannerAd.image_url.startsWith('/uploads/') ? bannerAd.image_url : (bannerAd.image_url.startsWith('/') ? bannerAd.image_url : bannerAd.image_url)}
                alt={bannerAd.title}
                className="max-h-full max-w-full object-contain group-hover:scale-105 transition-transform duration-300"
                onError={(e) => {
                  e.target.src = '/trainerlist-logo.png';
                }}
              />
            </div>
          )}
          
          {/* Content section */}
          <div className="flex-1 p-4 sm:p-6 text-center sm:text-left">
            <h3 className="text-lg sm:text-xl font-bold text-white mb-1 group-hover:text-red-400 transition-colors">
              {bannerAd.title}
            </h3>
            {bannerAd.company_name && (
              <p className="text-xs text-gray-400 mb-2">by {bannerAd.company_name}</p>
            )}
            {bannerAd.description && (
              <p className="text-sm text-gray-300 mb-3 line-clamp-2">{bannerAd.description}</p>
            )}
            {bannerAd.link_url && (
              <span className="inline-flex items-center gap-2 text-sm font-semibold text-red-400 group-hover:text-red-300 transition-colors">
                {bannerAd.link_text || 'Learn More'}
                <ExternalLink className="w-4 h-4" />
              </span>
            )}
          </div>
        </div>

        {/* Hover overlay effect */}
        <div className="absolute inset-0 bg-gradient-to-r from-red-600/0 via-red-600/5 to-red-600/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
      </div>
    </div>
  );
};

export default HomepageBannerAd;
